/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {FundReportActions} from "../fund.actions";
import {catchError, exhaustMap, map, of} from "rxjs";
import {Store} from "@ngrx/store";
import {FundReportService} from "../../services/fund-report.service";
import {tap} from "rxjs/operators";
import {selectSelectedFundReportId} from "./fund-report.selectors";
import {selectFundContext, selectSelectedFundId} from "../fund/fund.selectors";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpResponseError} from "../../../shared/model/http-response-error";
import {MatSnackBar} from "@angular/material/snack-bar";
import {concatLatestFrom} from "@ngrx/operators";
import {EMPTY_FUND_REPORT} from "../../models/fund-report";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";


// #########################################
// FUND REPORT CREATE
// #########################################
export const createFundReport = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router), fundReportService = inject(FundReportService)) => {
        return actions$.pipe(
            ofType(FundReportActions.create),
            exhaustMap((action) => {
                if (action.fundId) {
                    return fundReportService.createFundReport(
                        action.fundId,
                        {
                            ...EMPTY_FUND_REPORT,
                            ...{
                                reportDate: {
                                    ...EMPTY_FUND_REPORT.reportDate,
                                    date: action.reportDate
                                }
                            }
                        }
                    ).pipe(
                        map((fundReport) => FundReportActions.createSuccess({fundReport})),
                        tap((successAction) => router$.navigate([], {
                                queryParams: {fundReportId: successAction.fundReport.id, fundValuationId: null}, queryParamsHandling: "merge"
                            }
                        )),
                        catchError((errorResponse: any) => of(FundReportActions.createError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundReportActions.createError({errorMsg: "Found could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const showCreateSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundReportActions.createSuccess),
            tap((action) => {
                snackBar.open("Fund report successfully created.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showCreateError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundReportActions.createError),
            tap((action) => {
                snackBar.open("Fund report could not be created. " + action.errorMsg, "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);


// #########################################
// FUND REPORT CALC
// #########################################

export const calculateFundReport = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundReportService = inject(FundReportService)) => {
        return actions$.pipe(
            ofType(FundReportActions.calc),
            concatLatestFrom(() => store$.select(selectSelectedFundId)),
            exhaustMap(([action, fundId]) => {
                if (fundId && action.fundReport.id) {
                    return fundReportService.calculateFundReport(fundId, action.fundReport.id, action.fundReport).pipe(
                        map((fundReportResponse) => FundReportActions.calcSuccess({fundReport: fundReportResponse})),
                        catchError((errorResponse: any) => of(FundReportActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundReportActions.calcError({errorMsg: "No report for this fund available, please add a report first"}));
                }
            })
        );
    },
    {functional: true}
);

export const showCalcError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundReportActions.calcError),
            tap((action) => {
                snackBar.open("Fund report could not be calculated. " + action.errorMsg, "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND REPORT SAVE
// #########################################

export const saveFundReport = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundReportService = inject(FundReportService)) => {
        return actions$.pipe(
            ofType(FundReportActions.save),
            concatLatestFrom(() => [store$.select(selectFundContext), store$.select(selectSelectedSourceDataset)]),
            exhaustMap(([action, fundContext, dataset]) => {
                if (!!action.fundId && !!action.fundReport.id) {
                    if (action.fundReport.id !== fundContext.fundReportId) {
                        return of(FundReportActions.saveError({errorMsg: "Missmatch between Report IDs, please try again."}));
                    }
                    return fundReportService.updateFundReport(
                        action.fundId,
                        action.fundReport.id,
                        action.fundReport,
                        fundContext.fundValuationId,
                        fundContext.dealId,
                        dataset
                    ).pipe(
                        map((fundData) => FundReportActions.saveSuccess({fundData})),
                        catchError((errorResponse: any) => of(FundReportActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundReportActions.saveError({errorMsg: "No report for this fund available, please add a report first"}));
                }
            })
        );
    },
    {functional: true}
);

// #########################################
// FUND REPORT DELETE
// #########################################

export const deleteFundReport = createEffect(
    (actions$ = inject(Actions), fundReportService = inject(FundReportService)) => {
        return actions$.pipe(
            ofType(FundReportActions.delete),
            exhaustMap(action => {
                return fundReportService.deleteFundReport(action.fundId, action.fundReportId).pipe(
                    map(() => FundReportActions.deleteSuccess({fundId: action.fundId, fundReportId: action.fundReportId})),
                    catchError((errorResponse: any) => {
                        const httpResponseError: HttpResponseError = {
                            statusCode: errorResponse.status,
                            errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        }
                        return of(FundReportActions.deleteError({httpResponseError}));
                    }));
            })
        );
    },
    {functional: true}
);

export const showDeleteSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundReportActions.deleteSuccess),
            tap(() => {
                snackBar.open("Fund report deleted successfully.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const cleanupQueryParamsAfterDelete = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), router$ = inject(Router), route = inject(ActivatedRoute)) => {
        return actions$.pipe(
            ofType(FundReportActions.deleteSuccess),
            concatLatestFrom(() => store$.select(selectSelectedFundReportId)),
            tap(([action, selectedFundReportId]) => {
                if (selectedFundReportId === action.fundReportId) {
                    const queryParams = {...route.snapshot.queryParams};
                    delete queryParams["fundReportId"];
                    router$.navigate([], {relativeTo: route, queryParams, replaceUrl: true});
                }
            })
        );
    },
    {functional: true, dispatch: false}
);
